import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import HighQualityIcon from '@mui/icons-material/HighQuality';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import TimerIcon from '@mui/icons-material/Timer';
import BrushIcon from '@mui/icons-material/Brush';
import CategoryIcon from '@mui/icons-material/Category';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { SCUIGenericPagination } from '../../../atomic/atoms';
import AIStudioAnalyticsQuery from '../../../services/query/AIStudioAnalyticsQuery';
import TimeHelper from '../../../utils/time';
import { getModelTypeLabel } from '../../Mannequin/components/AIGeneration/AIGeneration.helper';

interface Transaction {
    id: number;
    predictionId: string;
    username: string;
    productBarcode: string;
    status: string;
    prompt: string;
    imageUrls: string;
    outputUrl: string;
    cost: number;
    predictionTimeSeconds: number;
    numImages: number;
    aspectRatio: string;
    outputFormat: string;
    resolution: string;
    errorMessage: string;
    retryAttempts: number;
    createdDate: string;
    updatedDate: string;
    completedDate: string;
    isInpainting?: boolean;
    modelType?: number; // 0: EachLabs, 1: Google Gemini
    generationTypeCode?: string;
    generationTypeName?: string;
}

interface TransactionResult {
    items: Transaction[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

interface UserTransactionDetailProps {
    username: string;
    startDate?: string;
    endDate?: string;
    productCode?: string;
}

export const UserTransactionDetail: FC<UserTransactionDetailProps> = ({ username, startDate, endDate, productCode }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [transactionDetails, setTransactionDetails] = useState<Record<number, Transaction>>({});
    const [loadingDetails, setLoadingDetails] = useState<Record<number, boolean>>({});

    useEffect(() => {
        setCurrentPage(1);
    }, [username, startDate, endDate, pageSize, productCode]);

    useEffect(() => {
        loadTransactions();
    }, [username, currentPage, startDate, endDate, pageSize, productCode]);

    const loadTransactions = async () => {
        try {
            setLoading(true);
            setError(null);
            const response: TransactionResult = await AIStudioAnalyticsQuery.GetUserTransactions({
                username,
                pageNumber: currentPage,
                pageSize: pageSize,
                startDate,
                endDate,
                productCode,
            });
            setTransactions(response.items || []);
            setTotalCount(response.totalCount || 0);
        } catch (err) {
            console.error('Error loading user transactions:', err);
            setError(t('aiStudioAnalytics.error'));
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        // Scroll to top of card
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setPageSize(newRowsPerPage);
    };

    const handleImageClick = (imageUrl: string) => {
        setPreviewImage(imageUrl);
    };

    const handleClosePreview = () => {
        setPreviewImage(null);
    };

    const handleAccordionChange = async (transactionId: number, isExpanded: boolean) => {
        if (isExpanded) {
            // Eğer bu transaction'ın detayı cache'de yoksa API'den al
            if (!transactionDetails[transactionId]) {
                try {
                    setLoadingDetails((prev) => ({ ...prev, [transactionId]: true }));
                    const detail: Transaction = await AIStudioAnalyticsQuery.GetTransactionDetail(transactionId);
                    setTransactionDetails((prev) => ({ ...prev, [transactionId]: detail }));
                } catch (err) {
                    console.error('Error loading transaction detail:', err);
                } finally {
                    setLoadingDetails((prev) => ({ ...prev, [transactionId]: false }));
                }
            }
        }
    };

    const getTransactionForDisplay = (transaction: Transaction): Transaction => {
        // Eğer detay yüklendiyse onu kullan, yoksa list'teki veriyi kullan (görseller olmadan)
        return transactionDetails[transaction.id] || transaction;
    };

    const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
        const statusMap: Record<string, 'success' | 'error' | 'warning' | 'info'> = {
            success: 'success',
            successful: 'success',
            error: 'error',
            failed: 'error',
            pending: 'warning',
            processing: 'info',
        };
        return statusMap[status.toLowerCase()] || 'default';
    };

    const getStatusLabel = (status: string): string => {
        const statusMap: Record<string, string> = {
            success: t('aiStudioAnalytics.successful'),
            successful: t('aiStudioAnalytics.successful'),
            error: t('aiStudioAnalytics.failed'),
            failed: t('aiStudioAnalytics.failed'),
            pending: t('general.pending'),
            processing: t('general.processing'),
        };
        return statusMap[status.toLowerCase()] || status;
    };

    const parseImageUrls = (imageUrls: string): string[] => {
        try {
            const parsed = JSON.parse(imageUrls);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            // Eğer JSON parse edilemiyorsa, tek bir URL olarak kullan
            return imageUrls ? [imageUrls] : [];
        }
    };

    if (loading) {
        return (
            <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                    <Alert severity="error">{error}</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: '24px !important' }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {username} - {t('aiStudioAnalytics.userTransactions')}
                    </Typography>
                    <Chip 
                        label={`${t('aiStudioAnalytics.totalTransactions')}: ${totalCount}`} 
                        sx={{ 
                            backgroundColor: '#0f172a', 
                            color: '#ffffff', 
                            fontWeight: 600,
                            borderRadius: '6px',
                            height: '28px',
                            fontSize: '0.8125rem'
                        }} 
                    />
                </Box>

                {transactions.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="textSecondary">{t('aiStudioAnalytics.noData')}</Typography>
                    </Box>
                ) : (
                    <Box>
                        {transactions.map((transaction, index) => {
                            const displayTransaction = getTransactionForDisplay(transaction);
                            const isLoading = loadingDetails[transaction.id];

                            return (
                                <Accordion
                                    key={transaction.id}
                                    sx={{
                                        mb: 1.5,
                                        borderRadius: '8px !important',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' }, // Remove MUI default divider line
                                        overflow: 'hidden',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                            borderColor: 'primary.light',
                                        },
                                    }}
                                    onChange={(_, isExpanded) => handleAccordionChange(transaction.id, isExpanded)}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'white',
                                            px: 2.5,
                                            py: 1,
                                            minHeight: 'unset',
                                            '& .MuiAccordionSummary-content': {
                                                width: '100%',
                                                overflow: 'hidden',
                                                margin: '0 !important',
                                            },
                                        }}
                                    >
                                        <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                            <Grid size={{ xs: 12, sm: 5, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                                                >
                                                    {t('aiStudioAnalytics.date')}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600,
                                                        color: 'text.primary',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    {TimeHelper.formatToTurkishDateTime(transaction.createdDate)}
                                                </Typography>
                                                {/* Type badges grouped under the date */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    {(transaction.generationTypeName || transaction.generationTypeCode) && (
                                                        <Chip
                                                            icon={<CategoryIcon sx={{ fontSize: '12px !important' }} />}
                                                            label={
                                                                transaction.generationTypeName || transaction.generationTypeCode
                                                            }
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.65rem', borderRadius: '4px' }}
                                                        />
                                                    )}
                                                    {transaction.isInpainting && (
                                                        <Chip
                                                            icon={<BrushIcon sx={{ fontSize: '12px !important' }} />}
                                                            label={t('aiStudioAnalytics.inpainting')}
                                                            size="small"
                                                            color="secondary"
                                                            variant="filled"
                                                            sx={{ height: 20, fontSize: '0.65rem', borderRadius: '4px' }}
                                                        />
                                                    )}
                                                    {transaction.modelType !== undefined && (
                                                        <Chip
                                                            icon={<SmartToyIcon sx={{ fontSize: '12px !important' }} />}
                                                            label={getModelTypeLabel(transaction.modelType, true)}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.65rem', borderRadius: '4px' }}
                                                        />
                                                    )}
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 3, md: 3 }} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                                                >
                                                    {t('aiStudioAnalytics.status')}
                                                </Typography>
                                                <Box>
                                                    <Chip
                                                        label={getStatusLabel(transaction.status)}
                                                        color={getStatusColor(transaction.status)}
                                                        size="small"
                                                        sx={{ height: '24px', fontSize: '0.75rem', fontWeight: 'bold' }}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 4, md: 5 }} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                                                >
                                                    {t('aiStudioAnalytics.productBarcode')}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600,
                                                        color: 'text.primary',
                                                        fontFamily: 'monospace',
                                                        letterSpacing: '0.5px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {transaction.productBarcode}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 2 }}>
                                        {isLoading ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    minHeight: 200,
                                                }}
                                            >
                                                <CircularProgress size={40} />
                                            </Box>
                                        ) : (
                                            <Grid container spacing={2}>
                                                {/* Sol taraf - Detaylar */}
                                                <Grid size={{ xs: 12, lg: 6 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        gutterBottom
                                                        sx={{ fontWeight: 'bold', mb: 2 }}
                                                    >
                                                        {t('aiStudioAnalytics.transactionDetails')}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 1.5,
                                                        }}
                                                    >
                                                        {/* Prediction ID */}
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                            <FingerprintIcon
                                                                sx={{
                                                                    fontSize: '1.2rem',
                                                                    color: 'primary.main',
                                                                    mt: 0.2,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.primary',
                                                                        fontWeight: 600,
                                                                        display: 'block',
                                                                        mb: 0.3,
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {t('aiStudioAnalytics.predictionId')}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.75rem',
                                                                        wordBreak: 'break-all',
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    {displayTransaction.predictionId}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Prompt */}
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                            <TextFieldsIcon
                                                                sx={{
                                                                    fontSize: '1.2rem',
                                                                    color: 'primary.main',
                                                                    mt: 0.2,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.primary',
                                                                        fontWeight: 600,
                                                                        display: 'block',
                                                                        mb: 0.3,
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {t('aiStudioAnalytics.prompt')}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        wordBreak: 'break-word',
                                                                        overflowWrap: 'break-word',
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    {displayTransaction.prompt}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Görsel Sayısı */}
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                            <ImageIcon
                                                                sx={{
                                                                    fontSize: '1.2rem',
                                                                    color: 'primary.main',
                                                                    mt: 0.2,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.primary',
                                                                        fontWeight: 600,
                                                                        display: 'block',
                                                                        mb: 0.3,
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {t('aiStudioAnalytics.numImages')}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    {displayTransaction.numImages}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* En Boy Oranı */}
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                            <AspectRatioIcon
                                                                sx={{
                                                                    fontSize: '1.2rem',
                                                                    color: 'primary.main',
                                                                    mt: 0.2,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.primary',
                                                                        fontWeight: 600,
                                                                        display: 'block',
                                                                        mb: 0.3,
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {t('aiStudioAnalytics.aspectRatio')}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    {displayTransaction.aspectRatio}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Çözünürlük */}
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                            <HighQualityIcon
                                                                sx={{
                                                                    fontSize: '1.2rem',
                                                                    color: 'primary.main',
                                                                    mt: 0.2,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.primary',
                                                                        fontWeight: 600,
                                                                        display: 'block',
                                                                        mb: 0.3,
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {t('aiStudioAnalytics.resolution')}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    {displayTransaction.resolution}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Çıktı Formatı */}
                                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                            <InsertPhotoIcon
                                                                sx={{
                                                                    fontSize: '1.2rem',
                                                                    color: 'primary.main',
                                                                    mt: 0.2,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.primary',
                                                                        fontWeight: 600,
                                                                        display: 'block',
                                                                        mb: 0.3,
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {t('aiStudioAnalytics.outputFormat')}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: '0.875rem',
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    {displayTransaction.outputFormat}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Üretim Tipi */}
                                                        {(displayTransaction.generationTypeName ||
                                                            displayTransaction.generationTypeCode) && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <CategoryIcon
                                                                    sx={{
                                                                        fontSize: '1.2rem',
                                                                        color: 'primary.main',
                                                                        mt: 0.2,
                                                                    }}
                                                                />
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.primary',
                                                                            fontWeight: 600,
                                                                            display: 'block',
                                                                            mb: 0.3,
                                                                            fontSize: '0.75rem',
                                                                        }}
                                                                    >
                                                                        {t('aiStudioAnalytics.generationType')}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontSize: '0.875rem',
                                                                            color: 'text.secondary',
                                                                        }}
                                                                    >
                                                                        {displayTransaction.generationTypeName ||
                                                                            displayTransaction.generationTypeCode}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {/* Model */}
                                                        {displayTransaction.modelType !== undefined && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <SmartToyIcon
                                                                    sx={{
                                                                        fontSize: '1.2rem',
                                                                        color: 'primary.main',
                                                                        mt: 0.2,
                                                                    }}
                                                                />
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.primary',
                                                                            fontWeight: 600,
                                                                            display: 'block',
                                                                            mb: 0.3,
                                                                            fontSize: '0.75rem',
                                                                        }}
                                                                    >
                                                                        {t('aiStudioAnalytics.model')}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontSize: '0.875rem',
                                                                            color: 'text.secondary',
                                                                        }}
                                                                    >
                                                                        {getModelTypeLabel(
                                                                            displayTransaction.modelType
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {/* Inpainting */}
                                                        {displayTransaction.isInpainting && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <BrushIcon
                                                                    sx={{
                                                                        fontSize: '1.2rem',
                                                                        color: 'secondary.main',
                                                                        mt: 0.2,
                                                                    }}
                                                                />
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.primary',
                                                                            fontWeight: 600,
                                                                            display: 'block',
                                                                            mb: 0.3,
                                                                            fontSize: '0.75rem',
                                                                        }}
                                                                    >
                                                                        {t('aiStudioAnalytics.productionMethod')}
                                                                    </Typography>
                                                                    <Chip
                                                                        icon={
                                                                            <BrushIcon
                                                                                sx={{ fontSize: '14px !important' }}
                                                                            />
                                                                        }
                                                                        label={t('aiStudioAnalytics.inpainting')}
                                                                        size="small"
                                                                        color="secondary"
                                                                        variant="filled"
                                                                        sx={{ height: 24, fontSize: '0.75rem' }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {/* İşlem Süresi */}
                                                        {displayTransaction.predictionTimeSeconds && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <TimerIcon
                                                                    sx={{
                                                                        fontSize: '1.2rem',
                                                                        color: 'primary.main',
                                                                        mt: 0.2,
                                                                    }}
                                                                />
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.primary',
                                                                            fontWeight: 600,
                                                                            display: 'block',
                                                                            mb: 0.3,
                                                                            fontSize: '0.75rem',
                                                                        }}
                                                                    >
                                                                        {t('aiStudioAnalytics.predictionTime')}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontSize: '0.875rem',
                                                                            color: 'text.secondary',
                                                                        }}
                                                                    >
                                                                        {displayTransaction.predictionTimeSeconds.toFixed(
                                                                            2
                                                                        )}
                                                                        s
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {/* Hata Mesajı */}
                                                        {displayTransaction.errorMessage && (
                                                            <Alert
                                                                severity="error"
                                                                sx={{ mt: 1, fontSize: '0.875rem' }}
                                                            >
                                                                <strong>{t('aiStudioAnalytics.errorMessage')}:</strong>{' '}
                                                                {displayTransaction.errorMessage}
                                                            </Alert>
                                                        )}
                                                    </Box>
                                                </Grid>

                                                {/* Sağ taraf - Görseller */}
                                                <Grid size={{ xs: 12, lg: 6 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        gutterBottom
                                                        sx={{ fontWeight: 'bold' }}
                                                    >
                                                        {t('aiStudioAnalytics.photos')}
                                                    </Typography>
                                                    <Box sx={{ mt: 2 }}>
                                                        {/* Girdi görselleri */}
                                                        {displayTransaction.imageUrls &&
                                                            parseImageUrls(displayTransaction.imageUrls).length > 0 && (
                                                                <Box sx={{ mb: 2 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ mb: 1, fontWeight: 'bold' }}
                                                                    >
                                                                        {t('aiStudioAnalytics.inputImages')}:
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            gap: 1,
                                                                            flexWrap: 'wrap',
                                                                        }}
                                                                    >
                                                                        {parseImageUrls(
                                                                            displayTransaction.imageUrls
                                                                        ).map((url, idx) => (
                                                                            <Box
                                                                                key={idx}
                                                                                onClick={() => handleImageClick(url)}
                                                                                sx={{ cursor: 'pointer' }}
                                                                            >
                                                                                <img
                                                                                    src={url}
                                                                                    alt={`input-${idx}`}
                                                                                    style={{
                                                                                        width: 80,
                                                                                        height: 80,
                                                                                        objectFit: 'cover',
                                                                                        borderRadius: 8,
                                                                                        border: '2px solid #e0e0e0',
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            )}

                                                        {/* Çıktı görselleri */}

                                                        {displayTransaction.outputUrl &&
                                                            parseImageUrls(displayTransaction.outputUrl).length > 0 && (
                                                                <Box>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ mb: 1, fontWeight: 'bold' }}
                                                                    >
                                                                        {t('aiStudioAnalytics.outputImage')}:
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            gap: 1,
                                                                            flexWrap: 'wrap',
                                                                        }}
                                                                    >
                                                                        {parseImageUrls(
                                                                            displayTransaction.outputUrl
                                                                        ).map((url, idx) => (
                                                                            <Box
                                                                                key={idx}
                                                                                onClick={() => handleImageClick(url)}
                                                                                sx={{ cursor: 'pointer' }}
                                                                            >
                                                                                <img
                                                                                    src={url}
                                                                                    alt={`output-${idx}`}
                                                                                    style={{
                                                                                        maxWidth: '100%',
                                                                                        height: 'auto',
                                                                                        maxHeight: 300,
                                                                                        objectFit: 'contain',
                                                                                        borderRadius: 8,
                                                                                        border: '2px solid #4caf50',
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            )}

                                                        {!displayTransaction.outputUrl &&
                                                            (!displayTransaction.imageUrls ||
                                                                parseImageUrls(displayTransaction.imageUrls).length ===
                                                                    0) && (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {t('aiStudioAnalytics.noPhotos')}
                                                                </Typography>
                                                            )}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Box>
                )}

                {/* Pagination */}
                {totalCount > 0 && (
                    <Box sx={{ mt: 4, pb: 2 }}>
                        <SCUIGenericPagination
                            count={totalCount}
                            page={currentPage}
                            rowsPerPage={pageSize}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            showPageInput={true}
                        />
                    </Box>
                )}

                {/* Image Preview Modal */}
                <Dialog
                    open={!!previewImage}
                    onClose={handleClosePreview}
                    maxWidth="lg"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: {
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                            },
                        },
                    }}
                >
                    <DialogContent
                        sx={{
                            p: 0,
                            position: 'relative',
                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <IconButton
                            onClick={handleClosePreview}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                color: 'white',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                                },
                                zIndex: 1,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        {previewImage && (
                            <img
                                src={previewImage}
                                alt="Preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '85vh',
                                    objectFit: 'contain',
                                }}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
};
